import { ChangeDetectionStrategy, Component, computed, effect, inject, type Signal, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RestrictedElementDirective } from '@auth';
import { APPLICATION_TYPE, type CanComponentDeactivate, formUtils } from '@core';
import { Store } from '@ngrx/store';
import { AuthSelectors, ClientManagementSelectors } from '@ngrx-store';
import { ConfirmationDialogComponent, NotificationsService, openUnsavedDataDialog, UnsavedDataDialogResponse } from '@ui';
import { deepEqual } from 'fast-equals';
import cloneDeep from 'lodash.clonedeep';
import { filter, firstValueFrom, switchMap } from 'rxjs';

import { MobileDashboardsSelectorComponent } from '../../components';
import { type Dashboard, type LayoutMode, type Widget } from '../../models';
import { type WidgetConfig } from '../../models/widget';
import { DashboardService } from '../../services';
import { LayoutComponent } from '../layout';
import { LayoutHelper } from '../layout/config';
import { WidgetDialogComponent } from '../widget-dialog';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LayoutComponent,
    MatButtonModule,
    MatCard,
    MatIcon,
    MatInputModule,
    ReactiveFormsModule,
    RouterLink,
    MobileDashboardsSelectorComponent,
    RestrictedElementDirective,
  ],
  selector: 'avs-li-dashboard-page',
  styleUrl: './dashboard-page.component.scss',
  templateUrl: './dashboard-page.component.html',
})
export class DashboardPageComponent implements CanComponentDeactivate {
  protected cloneId: Signal<string>;
  protected id: Signal<string>;
  protected mode: Signal<LayoutMode>;
  protected dashboard = signal<Dashboard | null>(null);
  protected nameControl = new FormControl('', [formUtils.removeSpacesValidator]);
  protected widgets = signal<Widget[]>([]);
  protected appType = inject(APPLICATION_TYPE);

  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notifications = inject(NotificationsService);
  private service = inject(DashboardService);
  private store = inject(Store);
  private isLoggedIn: Signal<boolean | undefined>;

  constructor() {
    const queryParams = toSignal(this.route.queryParams);
    const params = toSignal(this.route.params);
    const client = toSignal(this.store.select(ClientManagementSelectors.getCurrentClient));

    this.isLoggedIn = toSignal(this.store.select(AuthSelectors.isLoggedIn));
    this.id = computed(() => params()!['dashboardId']);
    this.cloneId = computed(() => queryParams()!['cloneId']);
    this.mode = computed(() => queryParams()!['mode'] ?? 'view');

    effect(() => {
      const id = this.id();
      const cloneId = this.cloneId();

      this.mode();

      if (!id) {
        return;
      }

      if (id === 'new' && !cloneId) {
        const newDashboard = {
          clientId: client()?.id,
          config: {
            widgets: [],
          },
          id,
          name: '',
        } as Dashboard;

        untracked(() => {
          this.dashboard.set(newDashboard);
          this.nameControl.patchValue(newDashboard.name);
          this.widgets.set([]);
        });

        return;
      }

      untracked(() => {
        this.service.getDashboardById(cloneId ?? id)
          .subscribe({
            error: () => this.router.navigateByUrl('/dashboard'),
            next: dashboard => {
              this.dashboard.set({ ...dashboard, id });
              this.widgets.set(cloneDeep(dashboard.config.widgets));
              this.nameControl.patchValue(cloneId ? `${dashboard.name} (cloned)` : dashboard.name);
            },
          });
      });
    });
  }

  canDeactivate() {
    if (!this.isLoggedIn()) {
      return true;
    }

    return this.handleUnsavedChanges();
  }

  async handleUnsavedChanges() {
    if (!this.hasUnsavedChanges()) {
      return true;
    }

    const result = await firstValueFrom(openUnsavedDataDialog(this.dialog, {
      description: 'Do you want to save them before leaving?',
      title: 'You have unsaved changes',
    }));

    if (result === UnsavedDataDialogResponse.SAVE) {
      await this.saveDashboard(false);
    }

    return result === UnsavedDataDialogResponse.SAVE || result === UnsavedDataDialogResponse.DISCARD;
  }

  protected hasUnsavedChanges() {
    if (!this.dashboard()) {
      return false;
    }

    if (this.dashboard()!.name !== this.nameControl.value) {
      return true;
    }

    return !deepEqual(this.dashboard()!.config.widgets, this.widgets());
  }

  protected addWidget() {
    this.dialog.open(WidgetDialogComponent)
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe((config: WidgetConfig) => {
        this.widgets.set([
          ...this.widgets(),
          LayoutHelper.createWidget(config),
        ]);
      });
  }

  protected async cancelEditing() {
    const result = await this.handleUnsavedChanges();

    if (!result) {
      return;
    }

    if (!this.dashboard()?.name) {
      await this.router.navigate(['/dashboard']);

      return;
    }

    this.widgets.set(cloneDeep(this.dashboard()!.config.widgets));

    await this.router.navigate(this.cloneId() ? ['dashboards', this.cloneId()] : [], {
      queryParams: {
        cloneId: undefined,
        mode: undefined,
      },
      queryParamsHandling: 'merge',
    });
  }

  protected delete() {
    const dashboard = this.dashboard()!;

    ConfirmationDialogComponent.open(this.dialog, {
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Delete',
      description: `Are you sure you want to delete [${dashboard.name}] dashboard?`,
      showWarning: true,
      title: 'Delete dashboard',
    })
      .pipe(
        filter(Boolean),
        switchMap(() => this.service.deleteDashboard(dashboard.id)),
      )
      .subscribe(async () => {
        await this.router.navigateByUrl('/dashboard');
        this.notifications.showSuccessMessage('Dashboard has been removed');
        this.service.changes$.next();
      });
  }

  protected deleteWidget(widget: Widget) {
    ConfirmationDialogComponent.open(this.dialog, {
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Delete',
      description: `Are you sure you want to delete [${widget.config.name}] widget?`,
      showWarning: true,
      title: 'Delete widget',
    })
      .pipe(filter(Boolean))
      .subscribe(() => {
        const widgets = this.widgets()
          .filter(w => w.id !== widget.id);

        this.widgets.set(widgets);
      });
  }

  protected editWidget(widget: Widget) {
    this.dialog.open(WidgetDialogComponent, {
      data: widget.config,
    })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe((config: WidgetConfig) => {
        const widgets = [...this.widgets()];

        widgets.splice(widgets.indexOf(widget), 1, {
          ...widget,
          config,
        });

        this.widgets.set(widgets);
      });
  }

  protected layoutChanged(widgets: Widget[]) {
    this.widgets.set(widgets);
  }

  protected async saveDashboard(shouldNavigate = true) {
    const dashboard = this.dashboard()!;
    const update = {
      config: {
        widgets: this.widgets(),
      },
      name: this.nameControl.value,
    } as Dashboard;

    const obs$ = dashboard.id === 'new'
      ? this.service.createDashboard(update)
      : this.service.updateDashboard(dashboard.id, update);

    const newDashboard = await firstValueFrom(obs$);

    this.dashboard.set(newDashboard ?? { ...dashboard, ...update });

    if (shouldNavigate) {
      await this.router.navigate(newDashboard ? ['dashboards', newDashboard.id] : [], {
        queryParams: {
          cloneId: undefined,
          mode: undefined,
        },
        queryParamsHandling: 'merge',
      });
    }
    this.notifications.showSuccessMessage('Dashboard has been saved');
    this.service.changes$.next();
  }
}
